<template>
  <div>
    <template v-if="ready">
      <div class="d-flex justify-content-between align-items-center px-2">
        <h5 class="mb-2">{{ $t("risk.select_form") || "Selecciona el/los formulario(s)" }}</h5>

        <b-button
          variant="secondary"
          to="/security-self-audit/templates/builder"
          target="_blank"
          v-if="$can ? $can('create', 'SecuritySelfAuditTemplate') : true"
        >
          <span class="text-nowrap text-capitalize">
            {{ $t("actions.add") + " Template" }}
          </span>
        </b-button>
      </div>

      <!-- Multiple template selector -->
      <div class="m-2">
        <v-select
          :key="'tpls-'+(normalizedTenantId===null?'all':normalizedTenantId)"
          v-model="itemData.ssa_templates"
          :options="filteredTemplates"
          :reduce="(t) => t.id"
          label="label"
          :placeholder="$t('risk.select_form_place_holder') || 'Selecciona uno o más templates...'"
          multiple
        />
      </div>

      <!-- Preview and answer button -->
      <div class="mt-2" v-if="selectedTemplates.length">
        <div class="d-flex justify-content-between align-items-center">
          <h5>{{ $t("risk.selected_form") || "Formularios seleccionados" }}</h5>

          <b-button
            variant="secondary"
            @click="onAnswerClick"
            :disabled="!mergedSnapshot.length || answering"
          >
            {{ answering ? ($t("loading") || "Cargando...") : ($t("risk.answer_form") || "Responder formulario") }}
          </b-button>
        </div>

        <!-- Preview questions -->
        <div class="mt-2">
          <h6 class="mt-1">{{ $tc("security_self_audit.template.question", 2) || "Preguntas" }}</h6>

          <div v-if="!mergedSnapshot.length" class="text-muted small">
            {{ $t("loading") || "Cargando..." }}
          </div>

          <ul v-else>
            <li v-for="(q, idx) in previewQuestions" :key="q.item_id || idx" class="mb-2">
              <p class="mb-1">
                <strong>{{ ($tc("security_self_audit.template.question", 1) || "Pregunta") + "-" + (idx + 1) }}:</strong>
                <span class="text-muted">
                  [{{ q.section || $t("risk.template.no_section") || "Sin sección" }}]
                </span>
                {{ q.name }}
              </p>
              <p class="mb-1">
                <strong>{{ $tc("custom_forms.types.title", 2) || "Tipos" }}:</strong>
                {{ q.type }}
              </p>

              <template v-if="q.options && q.options.fieldTypeCfg && q.options.fieldTypeCfg.data && q.options.fieldTypeCfg.data.length">
                <p class="mb-1">
                  <strong>{{ $t("security_self_audit.template.options") || "Opciones" }}:</strong>
                </p>
                <ul class="mb-0">
                  <li v-for="(opt, oIdx) in q.options.fieldTypeCfg.data" :key="oIdx">
                    {{ opt.name }}
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div class="text-center" v-else>
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import { shouldUpdateExistingRecordToInProgress } from "@/libs/risk/scheduledStartFormAccess";
import vSelect from "vue-select";
import { BButton, BSpinner } from "bootstrap-vue";

export default {
  name: "SecuritySelfAuditStep",
  components: { vSelect, BButton, BSpinner },
  props: {
    propItemData: { type: Object, required: true },
    existingAuditId: { type: Number, default: null },
    existingRecordLastStatusName: { type: String, default: null },
  },
  setup(props, { emit }) {
    const toast = useToast();

    // local state
    const itemData = ref({
      ...props.propItemData,
      ssa_templates: [],
      reference_number: props.propItemData?.reference_number || `SSA-${Date.now()}`,
    });

    const ready = ref(false);
    const answering = ref(false);

    const templates = ref([]);
    const mergedSnapshot = ref([]);

    // fetch templates list
    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get("/v1/security_self_audit_templates");
        templates.value = data.data || [];
      } catch (e) {
        console.error("Error fetching templates:", e);
        toast.error("Error al cargar los templates");
      }
    };

    // normalize tenant id
    const normalizedTenantId = computed(() => {
      const raw = itemData.value?.id_tenant ?? props.propItemData?.id_tenant ?? null;
      if (raw === null || raw === undefined || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    });

    // options for v-select
    const filteredTemplates = computed(() => {
      const tId = normalizedTenantId.value;
      return (templates.value || [])
        .filter((t) => tId === null || Number(t.id_tenant) === tId)
        .map((t) => ({ id: Number(t.id), label: t.name || t.description || `Template #${t.id}` }));
    });

    // selected template objects
    const selectedTemplates = computed(() => {
      const selIds = new Set((itemData.value.ssa_templates || []).map((x) => String(x)));
      const byId = new Map((templates.value || []).map((t) => [String(t.id), t]));
      return [...selIds].map((id) => byId.get(id)).filter(Boolean);
    });

    // deep clone util
    const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

    // namespace field ids per template
    const namespaceFieldIds = (tplId, fields) => {
      const list = Array.isArray(fields) ? fields : [];
      return list.map((f) => {
        const nf = deepClone(f);
        const oldId = nf.item_id;
        nf.item_id = `${tplId}:${oldId}`;
        if (Array.isArray(nf.show_if)) {
          nf.show_if = nf.show_if.map((c) =>
            c && c.field ? { ...c, field: `${tplId}:${c.field}` } : c
          );
        }
        return nf;
      });
    };

    // merge selected templates into one snapshot
    const mergeTemplatesToSnapshot = (tpls) => {
      const out = [];
      (tpls || []).forEach((tpl) => {
        const data = Array.isArray(tpl.data) ? tpl.data : [];
        namespaceFieldIds(tpl.id, data).forEach((f) => out.push(f));
      });
      return out;
    };

    const recomputeMergedSnapshot = () => {
      const sels = selectedTemplates.value;
      mergedSnapshot.value = sels.length ? mergeTemplatesToSnapshot(sels) : [];
    };

    // recompute on selection change
    watch(
      () => itemData.value.ssa_templates,
      (nv, ov) => { if (nv !== ov) recomputeMergedSnapshot(); }
    );

    // keep local itemData in sync with incoming props
    watch(
      () => props.propItemData,
      (nv) => {
        const prevId = itemData.value?.id;
        const prevTenant = itemData.value?.id_tenant;

        // merge new data
        itemData.value = {
          ...itemData.value,
          ...nv,
          reference_number: nv?.reference_number || itemData.value.reference_number,
        };

        // clear selection when unit or tenant changes
        const unitChanged = prevId !== nv?.id;
        const tenantChanged =
          (prevTenant ?? null) !== (nv?.id_tenant ?? null) &&
          (Number(prevTenant) || null) !== (Number(nv?.id_tenant) || null);

        if (unitChanged || tenantChanged) {
          itemData.value.ssa_templates = [];
          recomputeMergedSnapshot();
        }
      },
      { deep: true }
    );

    const createAudit = async () => {
      const payload = {
        reference_number: itemData.value.reference_number,
        id_unit: itemData.value.id,
      };
      const resp = await axios.post("/v1/security_self_audits", payload);
      return resp.data?.data?.id;
    };

    const resolveAuditId = async () => {
      if (props.existingAuditId != null && Number.isFinite(Number(props.existingAuditId))) {
        return Number(props.existingAuditId);
      }
      return createAudit();
    };

    const isExistingScheduledFlow = () =>
      props.existingAuditId != null && Number.isFinite(Number(props.existingAuditId));

    const updateStatusToInProgress = async (auditId) => {
      await axios.post(`/v1/security_self_audits/update_status/${auditId}`, { status_name: "in_progress" });
    };

    const onAnswerClick = async () => {
      if (!selectedTemplates.value.length || !mergedSnapshot.value.length) return;
      answering.value = true;
      try {
        const auditId = await resolveAuditId();
        if (!auditId) throw new Error("No se pudo crear la autoevaluación");
        if (
          isExistingScheduledFlow() &&
          shouldUpdateExistingRecordToInProgress(props.existingRecordLastStatusName)
        ) {
          await updateStatusToInProgress(auditId);
        }

        emit("open-audit-modal", {
          id: auditId,
          snapshot: mergedSnapshot.value,
          sources: (itemData.value.ssa_templates || []).slice(),
        });

        emit("assessment-complete");
      } catch (e) {
        console.error(e);
        const msg = e?.response?.data?.message || "No se pudo crear la autoevaluación";
        toast.error(msg);
      } finally {
        answering.value = false;
      }
    };

    const previewQuestions = computed(() => mergedSnapshot.value);

    onMounted(async () => {
      await fetchTemplates();
      ready.value = true;
    });

    return {
      // UI
      ready,
      answering,

      // data
      itemData,
      filteredTemplates,
      selectedTemplates,
      mergedSnapshot,
      previewQuestions,
      normalizedTenantId,

      // actions
      onAnswerClick,
    };
  },
};
</script>
